import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Select from '@radix-ui/react-select';
import { useFamilyStore } from '@/hooks/useStores';
import type { PersonResponse, UpdatePersonRequest } from 'we-tree-api-client/v2';
import type { LivingStatusType } from 'we-tree-api-client/v2';
import { GENDER_VALUES } from '@/types/gender';
import { parseDateOnly, formatDateForInput } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import ProfilePhotoUpload, { type PhotoDisplayStatus } from './ProfilePhotoUpload';

import './person-edit-form.css';

interface PersonEditFormProps {
  personData: PersonResponse;
  onSave: (updatedData: PersonResponse) => void;
  onCancel: () => void;
  familyId?: string;
  photoUrl?: string | null;
  photoDisplayStatus?: PhotoDisplayStatus;
  onPhotoUploadSuccess?: () => void;
}

const PersonEditForm: React.FC<PersonEditFormProps> = observer(
  ({ personData, onSave, onCancel, familyId, photoUrl, photoDisplayStatus, onPhotoUploadSuccess }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const familyStore = useFamilyStore();

    const initialBirthDate = useMemo(() => formatDateForInput(personData.birthDate) || '', [personData.birthDate]);
    const initialPrecision = (personData.birthDatePrecision ?? 'day') as DatePrecision;
    const initialDeathDate = useMemo(() => formatDateForInput(personData.deathDate) || '', [personData.deathDate]);
    const initialDeathPrecision = (personData.deathDatePrecision ?? 'day') as DatePrecision;
    const initialIsDeceased = personData.livingStatus === 'deceased';

    // Form state
    const [formData, setFormData] = useState<
      UpdatePersonRequest & { birthDatePrecision: DatePrecision; deathDatePrecision: DatePrecision }
    >({
      givenName: personData.givenName || '',
      middleName: personData.middleName || '',
      surname: personData.surname || '',
      suffix: personData.suffix || '',
      preferredName: personData.preferredName || '',
      gender: personData.gender,
      birthDate: initialBirthDate,
      birthDatePrecision: initialPrecision,
      birthPlace: personData.birthPlace || '',
      deathDate: initialDeathDate,
      deathDatePrecision: initialDeathPrecision,
      deathPlace: personData.deathPlace || ''
    });
    const [isDeceased, setIsDeceased] = useState(initialIsDeceased);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!formData.givenName?.trim()) {
        newErrors.givenName = t('person.givenNameRequiredError');
      }

      if (formData.birthDate && formData.deathDate) {
        const pb = parseDateOnly(formData.birthDate);
        const pd = parseDateOnly(formData.deathDate);
        if (pb && pd) {
          const birthCompare = pb.year * 10000 + pb.month * 100 + pb.day;
          const deathCompare = pd.year * 10000 + pd.month * 100 + pd.day;
          if (deathCompare < birthCompare) {
            newErrors.deathDate = t('person.deathAfterBirth');
          }
        }
      }
      // Death date only valid when marked deceased
      if (!isDeceased && formData.deathDate?.trim()) {
        newErrors.deathDate = t('person.deceasedRequiredForDeathDate');
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      if (!validate()) {
        return;
      }

      setIsSubmitting(true);

      try {
        const birthDateVal = formData.birthDate?.trim() || undefined;
        const deathDateVal = isDeceased ? formData.deathDate?.trim() || undefined : undefined;
        const updateRequest = {
          givenName: formData.givenName?.trim() || undefined,
          middleName: formData.middleName?.trim() || undefined,
          surname: formData.surname?.trim() || undefined,
          suffix: formData.suffix?.trim() || undefined,
          preferredName: formData.preferredName?.trim() || undefined,
          gender: formData.gender,
          birthDate: birthDateVal,
          birthDatePrecision: birthDateVal ? formData.birthDatePrecision : undefined,
          birthPlace: formData.birthPlace?.trim() || undefined,
          deathDate: deathDateVal,
          deathDatePrecision: deathDateVal ? formData.deathDatePrecision : undefined,
          deathPlace: isDeceased ? formData.deathPlace?.trim() || undefined : undefined,
          livingStatus: (isDeceased ? 'deceased' : 'living') as LivingStatusType
        };

        // Call store action to update person (handles API call, optimistic update, and rollback)
        const result = await familyStore.updatePerson(personData.familyMemberId, updateRequest);

        if (result.success) {
          onSave(result.data);
        } else {
          const error = result.error instanceof Error ? result.error : new Error(t('person.updateFailed'));
          setSubmitError(error.message || t('person.updateFailed'));
        }
      } catch (error) {
        setSubmitError(t('person.updateFailedRetry'));
        // eslint-disable-next-line no-console
        console.error('Person update error:', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleChange = (field: keyof UpdatePersonRequest, value: string | undefined) => {
      setFormData((prev) => ({ ...prev, [field]: value ?? '' }));
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

    const handleDeceasedChange = (checked: boolean) => {
      setIsDeceased(checked);
      if (!checked) {
        setFormData((prev) => ({ ...prev, deathDate: '', deathPlace: '' }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next.deathDate;
          return next;
        });
      }
    };

    return (
      <form onSubmit={handleSubmit} className="person-edit-form">
        <div className="person-edit-form__header">
          <h2 className="person-edit-form__title">{t('person.editTitle')}</h2>
          <div className="person-edit-form__header-actions">
            <button
              type="button"
              onClick={onCancel}
              className="person-edit-form__cancel-button"
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </button>
            <button type="submit" disabled={isSubmitting} className="person-edit-form__submit-button">
              {isSubmitting ? tCommon('saving') : t('actions.save')}
            </button>
          </div>
        </div>

        {submitError && (
          <div className="person-edit-form__error" role="alert">
            <span>{submitError}</span>
            <button
              type="button"
              onClick={() => setSubmitError(null)}
              className="person-edit-form__error-dismiss"
              aria-label={t('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        {familyId && (
          <div className="person-edit-form__photo-section">
            <ProfilePhotoUpload
              familyId={familyId}
              familyMemberId={personData.familyMemberId}
              canModify
              photoUrl={photoUrl}
              photoDisplayStatus={photoDisplayStatus}
              onUploadSuccess={onPhotoUploadSuccess}
              variant="edit"
            />
          </div>
        )}

        <div className="person-edit-form__section">
          <h3 className="person-edit-form__section-title">{t('person.basicInformation')}</h3>

          <div className="person-edit-form__field-group">
            <div className="person-edit-form__field">
              <label htmlFor="givenName" className="person-edit-form__label">
                {t('person.givenNameRequired')}
              </label>
              <input
                id="givenName"
                type="text"
                value={formData.givenName || ''}
                onChange={(e) => handleChange('givenName', e.target.value)}
                required
                disabled={isSubmitting}
                className={`person-edit-form__input ${errors.givenName ? 'person-edit-form__input--error' : ''}`}
              />
              {errors.givenName && <span className="person-edit-form__error-message">{errors.givenName}</span>}
            </div>

            <div className="person-edit-form__field">
              <label htmlFor="middleName" className="person-edit-form__label">
                {t('person.middleName')}
              </label>
              <input
                id="middleName"
                type="text"
                value={formData.middleName || ''}
                onChange={(e) => handleChange('middleName', e.target.value)}
                disabled={isSubmitting}
                className="person-edit-form__input"
              />
            </div>

            <div className="person-edit-form__field">
              <label htmlFor="surname" className="person-edit-form__label">
                {t('person.surname')}
              </label>
              <input
                id="surname"
                type="text"
                value={formData.surname || ''}
                onChange={(e) => handleChange('surname', e.target.value)}
                disabled={isSubmitting}
                className="person-edit-form__input"
              />
            </div>
          </div>

          <div className="person-edit-form__field-group">
            <div className="person-edit-form__field">
              <label htmlFor="suffix" className="person-edit-form__label">
                {t('person.suffix')}
              </label>
              <input
                id="suffix"
                type="text"
                value={formData.suffix || ''}
                onChange={(e) => handleChange('suffix', e.target.value)}
                disabled={isSubmitting}
                className="person-edit-form__input"
                placeholder={t('person.suffixPlaceholder')}
              />
            </div>

            <div className="person-edit-form__field">
              <label htmlFor="preferredName" className="person-edit-form__label">
                {t('person.preferredName')}
              </label>
              <input
                id="preferredName"
                type="text"
                value={formData.preferredName || ''}
                onChange={(e) => handleChange('preferredName', e.target.value)}
                disabled={isSubmitting}
                className="person-edit-form__input"
              />
            </div>

            <div className="person-edit-form__field">
              <label htmlFor="gender" className="person-edit-form__label">
                {t('person.gender')}
              </label>
              <Select.Root
                value={formData.gender || 'unknown'}
                onValueChange={(v) => handleChange('gender', v)}
                disabled={isSubmitting}
              >
                <Select.Trigger id="gender" className="form-select-trigger" aria-label={t('person.gender')}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {GENDER_VALUES.map((gender) => (
                      <Select.Item key={gender} value={gender} className="form-select-item">
                        <Select.ItemText>{t(`gender.${gender}`)}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          </div>
        </div>

        <div className="person-edit-form__section">
          <h3 className="person-edit-form__section-title">{t('person.birthInformation')}</h3>

          <div className="person-edit-form__field-group">
            <DateFieldWithPrecision
              id="birthDate"
              label={t('person.birthDate')}
              value={formData.birthDate ?? ''}
              precision={formData.birthDatePrecision}
              onValueChange={(v) => setFormData((prev) => ({ ...prev, birthDate: v }))}
              onPrecisionChange={(p) => setFormData((prev) => ({ ...prev, birthDatePrecision: p }))}
              onErrorClear={() =>
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.birthDate;
                  return next;
                })
              }
              disabled={isSubmitting}
              error={errors.birthDate}
              allowClearViaInput={false}
              layout="inline"
            />

            <div className="person-edit-form__field">
              <label htmlFor="birthPlace" className="person-edit-form__label">
                {t('person.birthPlace')}
              </label>
              <input
                id="birthPlace"
                type="text"
                value={formData.birthPlace || ''}
                onChange={(e) => handleChange('birthPlace', e.target.value)}
                disabled={isSubmitting}
                className="person-edit-form__input"
              />
            </div>
          </div>
        </div>

        <div className="person-edit-form__section">
          <h3 className="person-edit-form__section-title">{t('person.deathInformation')}</h3>

          <div className="person-edit-form__field-group">
            <div className="person-edit-form__field person-edit-form__field--full">
              <label className="person-edit-form__label person-edit-form__checkbox-label">
                <input
                  type="checkbox"
                  checked={isDeceased}
                  onChange={(e) => handleDeceasedChange(e.target.checked)}
                  disabled={isSubmitting}
                  className="person-edit-form__checkbox"
                  aria-describedby="deceased-desc"
                />
                <span>{t('person.deceased')}</span>
              </label>
              <span id="deceased-desc" className="person-edit-form__hint">
                {t('person.deceasedHintExtended')}
              </span>
            </div>
          </div>

          {isDeceased && (
            <div className="person-edit-form__field-group">
              <DateFieldWithPrecision
                id="deathDate"
                label={t('person.deathDate')}
                value={formData.deathDate ?? ''}
                precision={formData.deathDatePrecision}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, deathDate: v }))}
                onPrecisionChange={(p) => setFormData((prev) => ({ ...prev, deathDatePrecision: p }))}
                onErrorClear={() =>
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.deathDate;
                    return next;
                  })
                }
                disabled={isSubmitting}
                error={errors.deathDate}
                hint={t('person.dateOptionalHint')}
                allowClearViaInput={true}
                layout="inline"
              />

              <div className="person-edit-form__field">
                <label htmlFor="deathPlace" className="person-edit-form__label">
                  {t('person.deathPlace')}
                </label>
                <input
                  id="deathPlace"
                  type="text"
                  value={formData.deathPlace || ''}
                  onChange={(e) => handleChange('deathPlace', e.target.value)}
                  disabled={isSubmitting}
                  className="person-edit-form__input"
                />
              </div>
            </div>
          )}
        </div>

        <div className="person-edit-form__actions">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="person-edit-form__button person-edit-form__button--cancel"
          >
            {tCommon('cancel')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="person-edit-form__button person-edit-form__button--submit"
          >
            {isSubmitting ? tCommon('saving') : t('actions.save')}
          </button>
        </div>
      </form>
    );
  }
);

export default PersonEditForm;
